import React, { useState, useEffect } from "react";
import { web3, getCurrentAccount } from "./Web3";
import captureImage from './Capture1.PNG'; // Image when MetaMask is not installed
const contractAddress = "0x1314C776D0444c8b85D7A5c5D730A5AfB82548B4"; // Replace with your contract address
const contractABI = [
  {
    "inputs": [],
    "name": "deposit",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function",
  },
  {
    "inputs": [
      { "internalType": "uint256", "name": "_amount", "type": "uint256" },
    ],
    "name": "withdraw",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function",
  },
  {
    "inputs": [],
    "name": "requestLoan",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function",
  },
  {
    "inputs": [
      { "internalType": "address", "name": "_user", "type": "address" },
    ],
    "name": "approveLoan",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function",
  },
  {
    "inputs": [],
    "name": "repayLoan",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function",
  },
  {
    "inputs": [],
    "name": "getLoanStatus",
    "outputs": [
      { "internalType": "bool", "name": "", "type": "bool" },
    ],
    "stateMutability": "view",
    "type": "function",
  },
  {
    "inputs": [],
    "name": "getPendingLoanAmount",
    "outputs": [
      { "internalType": "uint256", "name": "", "type": "uint256" },
    ],
    "stateMutability": "view",
    "type": "function",
  },
];
const contract = new web3.eth.Contract(contractABI, contractAddress);

const LoanSection = () => {
  const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState(false);
  const [loanAmount, setLoanAmount] = useState(""); // Loan amount input by user
  const [loanStatus, setLoanStatus] = useState("");
  const [balance, setBalance] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [userAddress, setUserAddress] = useState("");  // Address for loan approval check
  const [loanApprovalStatus, setLoanApprovalStatus] = useState(""); // Display loan approval status
  const [approvedLoanAmount, setApprovedLoanAmount] = useState(0); // Track the approved loan amount
  const [pendingLoanAmount, setPendingLoanAmount] = useState(0); // Track the pending loan amount
  const [repaymentAmount, setRepaymentAmount] = useState(""); // Amount user intends to repay

  // Fetch balance
  const fetchBalance = async (account) => {
    try {
      const balanceWei = await web3.eth.getBalance(account);
      const balanceEth = web3.utils.fromWei(balanceWei, "ether");
      setBalance(balanceEth); // Update balance in ETH
    } catch (error) {
      console.error("Error fetching balance:", error);
      setErrorMessage("Error fetching balance: " + error.message);
    }
  };

  const deposit = async () => {
    try {
      const account = await getCurrentAccount();
      if (!account) {
        alert("Please connect to MetaMask");
        return;
      }
      await contract.methods.deposit().send({
        from: account,
        value: web3.utils.toWei(loanAmount, "ether"),
      });
      fetchBalance(account); // After deposit, update balance
      alert("Deposit successful!");
    } catch (error) {
      console.error("Error during deposit:", error);
      setErrorMessage("Error during deposit: " + error.message);
    }
  };

  // Withdraw function
  const withdraw = async () => {
    try {
      const account = await getCurrentAccount();
      if (!account) {
        alert("Please connect to MetaMask");
        return;
      }
      await contract.methods
        .withdraw(web3.utils.toWei(loanAmount, "ether"))
        .send({ from: account });
      fetchBalance(account); // After withdraw, update balance
      alert("Withdraw successful!");
    } catch (error) {
      console.error("Error during withdraw:", error);
      setErrorMessage("Error during withdraw: " + error.message);
    }
  };

  // Request loan function
  const requestLoan = async () => {
    try {
      const account = await getCurrentAccount();
      if (!account) {
        alert("Please connect to MetaMask");
        return;
      }
      await contract.methods
        .requestLoan(web3.utils.toWei(loanAmount, "ether"))
        .send({ from: account });
      alert("Loan requested successfully!");
    } catch (error) {
      console.error("Error during loan request:", error);
      setErrorMessage("Error during loan request: " + error.message);
    }
  };

  // Repay loan function
  const repayLoan = async () => {
    try {
      const account = await getCurrentAccount();
      if (!account) {
        alert("Please connect to MetaMask");
        return;
      }
      // Convert repayment amount from Ether to Wei
      const repaymentInWei = web3.utils.toWei(repaymentAmount, "ether");
      await contract.methods.repayLoan().send({
        from: account,
        value: repaymentInWei,
      });
      // Update the pending loan amount after repayment
      fetchPendingLoanAmount(account);
      alert("Loan repaid successfully!");
    } catch (error) {
      console.error("Error during loan repayment:", error);
      setErrorMessage("Error during loan repayment: " + error.message);
    }
  };

  // Approve loan for user based on balance
  const approveLoan = async () => {
    try {
      const account = await getCurrentAccount();
      if (!account) {
        alert("Please connect to MetaMask");
        return;
      }
      if (!userAddress) {
        alert("Please enter a valid user address");
        return;
      }
      // Check the user's balance using web3.eth.getBalance
      const userBalanceWei = await web3.eth.getBalance(userAddress);
      const userBalanceEth = web3.utils.fromWei(userBalanceWei, "ether");
      if (parseFloat(userBalanceEth) >= parseFloat(loanAmount)) {
        // If the user has enough balance, approve the loan
        await contract.methods.approveLoan(userAddress).send({ from: account });
        setApprovedLoanAmount(loanAmount); // Store the approved loan amount
        setLoanApprovalStatus("Loan approved for " + userAddress);
      } else {
        setApprovedLoanAmount(0); // Reset approved loan amount
        setLoanApprovalStatus("Loan rejected: User does not have enough balance.");
      }
    } catch (error) {
      console.error("Error during loan approval:", error);
      setErrorMessage("Error during loan approval: " + error.message);
    }
  };

  // Fetch the pending loan amount
  const fetchPendingLoanAmount = async (account) => {
    try {
      const pendingAmount = await contract.methods.getPendingLoanAmount().call({
        from: account,
        gas: 3000000,
      });
      console.log("Pending loan amount:", pendingAmount);
      setPendingLoanAmount(web3.utils.fromWei(pendingAmount, "ether"));
    } catch (error) {
      setErrorMessage("Error fetching pending loan amount: " + error.message);
      // Log additional error details for debugging
    }
  };

  // useEffect to check the pending loan amount on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      const account = await getCurrentAccount();
      if (account) {
        fetchBalance(account);
        fetchPendingLoanAmount(account);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (window.ethereum) {
      setIsMetaMaskInstalled(true);  // MetaMask is installed
    } else {
      setIsMetaMaskInstalled(false); // MetaMask is not installed
    }
  }, []);

  return (
    <div>
      <h3>Bank Loan Section</h3>

      {/* Display this message and image only if MetaMask is not installed */}
      {!isMetaMaskInstalled && (
        <div>
          <p>MetaMask is not installed. Please install it to use the application.</p>
          <img src={captureImage} alt="MetaMask not installed" />
        </div>
      )}

      {/* Display this content only if MetaMask is installed */}
      {isMetaMaskInstalled && (
        <div>
          <input
            type="text"
            placeholder="Enter loan amount (ETH)"
            value={loanAmount}
            onChange={(e) => setLoanAmount(e.target.value)}
          />
          <div>
            <input
              type="text"
              placeholder="Enter user address for loan approval"
              value={userAddress}
              onChange={(e) => setUserAddress(e.target.value)}
            />
          </div>
          <div>
            <button onClick={deposit}>Deposit</button>
            <button onClick={withdraw}>Withdraw</button>
            <button onClick={requestLoan}>Request Loan</button>
            <button onClick={repayLoan}>Repay Loan</button>
            <button onClick={approveLoan}>Approve Loan</button>
          </div>
          <div>
            <p>Current Balance: {balance} ETH</p>
            <p>Requested Loan Amount: {loanAmount} ETH</p>
            <p>Approved Loan Amount: {approvedLoanAmount} ETH</p>
            <p>Pending Loan Amount: {pendingLoanAmount} ETH</p>
            <p>Loan Status: {loanStatus}</p>
            <p>{loanApprovalStatus}</p>
          </div>
          <div>
            <input
              type="text"
              placeholder="Enter repayment amount (ETH)"
              value={repaymentAmount}
              onChange={(e) => setRepaymentAmount(e.target.value)}
            />
          </div>
          {errorMessage && <p>{errorMessage}</p>}
        </div>
      )}
    </div>
  );
};

export default LoanSection;
